import { h, Component } from 'preact';
import Store from 'store/store';
import * as actions from 'store/actions';
import Reveal from 'components/shared/reveal/reveal';
import HTML from 'components/core/html/html';
import { Link } from 'components/core/link/link.js';
import Expander from 'components/shared/expander/expander';
import pure from 'utils/pure';
import { getPath } from 'utils/clientUtils';
import { asList, joinClasses, parseDate } from 'utils/utils';
import formatDateTime from 'utils/formatDateTime';
import s from 'components/shared/calendarRemind/calendarRemind.sss';
import { track } from 'utils/tracking';

const MINUTE = 60 * 1000;


export default @pure class CalendarRemind extends Component {

	constructor(props) {
		super(props);
		this.calendars = [];
	}

	componentWillMount() {
		this.createICS('iCal', 'Apple iCal', 'iCal.png');
		this.createICS('outlookCal', 'Microsoft Outlook', 'outlookCal.png');
		this.createICS('iCalAndOutlook', 'Outlook / iCal', 'iCal.png');
		this.createICS('yahoo', 'Yahoo', 'yahoomail.svg');
		this.refreshCalendars();
	}

	componentDidMount() {
		const standalone = window.navigator.standalone;
		const userAgent = window.navigator.userAgent.toLowerCase();
		const safari = /safari/.test(userAgent);
		const ios = /iphone|ipod|ipad/.test(userAgent);

		if (ios && !standalone && !safari) {
			this.setState({
				isWebview: true
			});
		}
	}

	componentWillUpdate(props) {
		this.refreshCalendars(props);
	}

	refreshCalendars(props) {
		props = props || this.props;
		let pageData = props.pageData || {};

		const releaseDateDate = parseDate(props.customDate || props.releaseDate);
		if (!releaseDateDate || Date.now() > releaseDateDate) {
			// Release date has passed
			this.calendars = [];
			return;
		}
		let endDate = new Date(releaseDateDate);
		endDate.setDate(endDate.getDate() + 1);
		const releaseDateData = {
			start: releaseDateDate.getTime(),
			end: endDate.getTime()
		};

		this.calendars = generateCalendars(
			pageData.calendars,
			props.copy,
			releaseDateData,
			props.appUrl,
			props.sharedImageRoot,
			props.customTitle,
			props.customDescription
		);
	}

	calendarOnClick(child) {
		// Let the store know
		Store.emit(actions.CALENDAR_CLICKED, child, this.props.activePage.id);

		// Send generic calendar tracking event
		track({ a: 'calendar_remind_clicked', 'l': child.id });

		if (child.calendarContent && this.props.client && this.props.client.isIE && !this.props.client.isEdge) {
			const blob = new Blob([child.calendarContent], { type: 'octet/stream' });
			window.navigator.msSaveBlob(blob, 'calendar.ics');
		}
	}

	getLinkList() {
		let props = this.props;
		let client = props.client || {};

		const calendarFilename = 'calendar.ics';
		let activePath = getPath(props.activePage) || '';
		const relativeFilePath = activePath.slice(1).split('/').map(() => '').join('../');
		const calendarFilePath = relativeFilePath + calendarFilename;

		return [].concat(this.calendars.map(child => {
			let image;
			if (child.imgPath) {
				image = <img class={s.icon} alt={child.title ? child.title : ''} src={child.imgPath} />;
			}

			// Killian // hide outlook for IE, currently not working
			if (child.id === 'outlook' && (client.isIE && !client.isEdge)) {
				return;
			}

			// LUKE // For icals use the static .ics file
			if (child.id === 'ical') {
				// can't download files if it's a webview, so don't even render button
				if (this.state.isWebview) {
					return;
				}
				// LUKE // hide the ical when the user is on chrome and iOS (currently doesn't work)
				if (client.isChrome && client.device === 'iphone') {
					return;
				}
				child.href = calendarFilePath;
			}

			// neeh 10-Jul-18: Fix a bug that was closing the .ics 1 sec after opening on iOS
			let target = '_blank';
			if (child.id === 'ical' || child.id === 'outlook') {
				target = '_self';
			}
			if (child.target) {
				target = child.target;
			}

			return (
				<Link
					href={child.href}
					title={child.title}
					class={joinClasses(s.calWrapper, child.class, !props.useReveal && s.calOption)}
					target={target}
					id={'calendar_remind_button_' + child.id}
					trackId={child.id + '_release_date_clicked'}
					extraOnClick={this.calendarOnClick.bind(this, child)}
					download={child.download}
					renderCustomChild
				>
					{image}
					<p class={s.text}>{child.title}</p>
				</Link>
			);
		}));
	}

	renderCta() {
		if (this.props.copy.$CTA_TITLE)
			return <HTML tag="h2" class={s.reminder} content={this.props.copy.$CTA_TITLE} markdown />;
	}

	renderContent() {
		let props = this.props;
		let ContentComponent;
		let contentProps;
		if (props.useReveal) {
			ContentComponent = Reveal;
			contentProps = {
				revealClass: joinClasses(s.remindWrapper, s.reveal)
			};
		} else {
			ContentComponent = Expander;
			contentProps = {
				expandClass: joinClasses(s.remindWrapper, s.expand)
			};
		}
		return (
			<ContentComponent
				linkList={this.getLinkList()}
				sourceContainer="calendar"
				buttonText={props.copy.$REMIND_ME}
				customPrompt={props.customPrompt}
				{...contentProps}
			/>
		);
	}

	createICS(id, ...args) {
		return calendarGenerators[id] = (...base) => calendarGenerators.ics(...base, id, ...args);
	}

	render(props) {
		// Moved expandWrapper class from expander.sss to here
		let classWrapper;
		const isReveal = props.useReveal;
		if (!isReveal) {
			classWrapper = s.expandWrapper;
		} else {
			classWrapper = s.wrapper;
		}

		if (!this.calendars.length) {
			return <div />;
		}

		return (
			<div class={joinClasses(s.container, props.class)}>
				<div class={classWrapper}>
					{this.renderCta()}
					{this.renderContent()}
				</div>
			</div>
		);
	}
}

const formatTime = function(timestamp, withTime) {
	return formatDateTime(new Date(+timestamp), 'yyyyMMdd' + (withTime ? '\\THHmmss' : ''));
};

const calculateEndTime = function(event, withTime) {
	if (!event.start && !event.end) return formatTime(Date.now(), withTime);
	return event.end ?
		formatTime(event.end, withTime) :
		formatTime(new Date(event.start.getTime() + (event.duration * MINUTE)), withTime);
};

const calendarGenerators = {
	googleCal: function(copy, event, appUrl, sharedImageRoot, customTitle, customDescription) {
		const startTime = formatTime(event.start);
		const endTime = calculateEndTime(event);

		const cal = {
			title: 'Google Calendar',
			startTime: startTime,
			endTime: endTime,
			href: (
				[
					'https://www.google.com/calendar/render',
					'?action=TEMPLATE',
					'&text=' + encodeURIComponent(customTitle || copy.$RELEASE_DATE_TEXT || ''),
					'&dates=' + (startTime || ''),
					'/' + (endTime || ''),
					'&details=' + encodeURIComponent(customDescription || [copy.$RELEASE_DESCRIPTION, appUrl].filter(e => e).join(' ')),
					'&sprop=&sprop=name:'
				].join('')
			),
			class: s.googleCal,
			imgPath: `${sharedImageRoot}/googleCal.png`,
			trackId: 'addGoogleCal_button',
			id: 'google'
		};

		return cal;
	},

	ics: function(copy, event, appUrl, sharedImageRoot, customTitle, customDescription, id, calendarName, imgPath) {
		const startTime = formatTime(event.start);
		const endTime   = calculateEndTime(event);
		let target = false;
		const calendarContent = [
			'BEGIN:VCALENDAR',
			'VERSION:2.0',
			'PRODID:-//powster//NONSGML v1.0//EN',
			'BEGIN:VEVENT',
			'URL;VALUE=URI:' + customDescription ? '' : appUrl,
			'DTSTART;VALUE=DATE:' + (startTime || ''),
			'DTEND:' + (endTime || ''),
			'SUMMARY:' + (customTitle || copy.$RELEASE_DATE_TEXT || ''),
			'DESCRIPTION:' + (customDescription || ((copy.$RELEASE_DESCRIPTION ? copy.$RELEASE_DESCRIPTION + ' ' : '') + appUrl)),
			'END:VEVENT',
			'END:VCALENDAR',
			''
		].join(typeof navigator !== 'undefined' && navigator.appVersion.indexOf('Win') !== -1 ? '\r\n' : '\n');
		let href = 'data:text/calendar;charset=utf8,' + encodeURIComponent(calendarContent);
		const client = Store.get().client;
		if (client && client.isIE && !client.isEdge && typeof Blob !== 'undefined') {
			href = false;
			target = '_self';
		}
		if (client.isEdge) {
			const blob = new Blob([calendarContent], { type: 'octet/stream' });
			href = window.URL.createObjectURL(blob);
		}
		id = id || 'ics';
		return {
			title: calendarName,
			startTime: startTime,
			endTime: endTime,
			href: href,
			class: s[id],
			imgPath: `${sharedImageRoot}/${imgPath || 'ics.png'}`,
			trackId: `add_${id}_calendar_button`,
			id: id,
			download: 'calendar.ics',
			calendarContent,
			target
		};
	}
};

const generateCalendars = function(calendars, copy, event, appUrl, sharedImageRoot, customTitle, customDescription) {
	if (!calendars) {
		return [];
	}
	let priority = ['googleCal', 'iCal', 'outlookCal', 'yahoo'].reverse();
	return asList(calendars).sort((a, b) => priority.indexOf(b) - priority.indexOf(a)).map(cal => {
		let fn = calendarGenerators[cal];
		return typeof fn === 'function' && fn(copy, event, appUrl, sharedImageRoot, customTitle, customDescription);
	}).filter(e => e);
};
